import { type FC, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { InputGroup } from 'react-bootstrap';
import { Draggable } from 'react-beautiful-dnd';
import { BsX } from 'react-icons/bs';

import CodeMirrorHTML from '@editor2/components/CodeMirrorHTML';
import { IconCorrect, IconIncorrect } from '@editor2/icons';
import { type MCChoicePayload, type MCQuestion } from '@editor2/types/pageElements';
import { updateElement } from '@editor2/reducers/editor/editorSlice';

import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';
import { useDebounceProtectedState } from '@editor2/hooks/useDebounceProtectedState';

interface Props {
	element: MCQuestion;
	choice: MCChoicePayload;
	courseId: number;
	index: number;
}

const McChoice: FC<Props> = (props) => {
	const { element, choice, courseId, index } = props;

	const commonPayload = { elementId: element.family_id, courseId };

	const dispatch = useDispatch();
	const debouncedDispatch = useDebouncedDispatch();

	// Mapper for hook consistency - simply extracts the fields we need to track locally
	const choiceMapper = useCallback(
		({ body, rejoinder, is_correct }: MCChoicePayload) => ({ body, rejoinder, is_correct }),
		[]
	);

	const [choiceState, handleLocalChange] = useDebounceProtectedState(choice, choiceMapper);

	const { body: choiceText, rejoinder: choiceRejoinder, is_correct: isCorrect } = choiceState;

	const handleChoiceChange = (value: string) => {
		handleLocalChange({ body: value, rejoinder: choiceRejoinder, is_correct: isCorrect });
		debouncedDispatch(
			updateElement({
				...commonPayload,
				choices: [
					...element.choices.slice(0, index),
					{ ...choice, body: value },
					...element.choices.slice(index + 1)
				]
			})
		);
	};

	const handleRejoinderChange = (value: string) => {
		handleLocalChange({ body: choiceText, rejoinder: value, is_correct: isCorrect });
		debouncedDispatch(
			updateElement({
				...commonPayload,
				choices: [
					...element.choices.slice(0, index),
					{ ...choice, rejoinder: value },
					...element.choices.slice(index + 1)
				]
			})
		);
	};

	const handleIsCorrectChange = () => {
		const newIsCorrect = !isCorrect;
		handleLocalChange({
			body: choiceText,
			rejoinder: choiceRejoinder,
			is_correct: newIsCorrect
		});
		dispatch(
			updateElement({
				...commonPayload,
				choices: [
					...element.choices.slice(0, index),
					{ ...choice, is_correct: newIsCorrect },
					...element.choices.slice(index + 1)
				]
			})
		);
	};

	const onDeleteChoiceClick = (index: number) => {
		// eslint-disable-next-line no-alert
		if (window.confirm(`Are you sure to delete the answer?`)) {
			dispatch(
				updateElement({
					...commonPayload,
					choices: [...element.choices.slice(0, index), ...element.choices.slice(index + 1)]
				})
			);
		}
	};

	return (
		<Draggable draggableId={choice.family_id} index={index}>
			{(provided) => (
				<div ref={provided.innerRef} {...provided.draggableProps}>
					<div className="choice">
						<InputGroup className="choiceGroup">
							<InputGroup.Prepend {...provided.dragHandleProps}>
								<InputGroup.Checkbox checked={isCorrect} onChange={handleIsCorrectChange} />
							</InputGroup.Prepend>{' '}
							<CodeMirrorHTML
								value={choiceText}
								showTips={false}
								options={{ placeholder: 'Answer', lineNumbers: false, gutters: [] }}
								onChange={(value) => handleChoiceChange(value)}
							/>
						</InputGroup>

						<div className="rejoinder">
							<div {...provided.dragHandleProps} className="draggableBlock">
								{isCorrect ? <IconCorrect /> : <IconIncorrect />}
							</div>
							<CodeMirrorHTML
								value={choiceRejoinder}
								showTips={false}
								options={{ placeholder: 'Rejoinder', lineNumbers: false, gutters: [] }}
								onChange={(value) => handleRejoinderChange(value)}
							/>
						</div>

						<BsX className="deleteChoiceIcon" onClick={() => onDeleteChoiceClick(index)} />
					</div>
				</div>
			)}
		</Draggable>
	);
};

export default McChoice;
